import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Stack,
  Avatar,
} from '@mui/material';
import { Clock, Edit2, Info, HelpCircle } from 'lucide-react';
import { toast } from '../../../../../../../../ui/toast';
import { assessmentBEIQuestion } from '../../../../../../../../utils/api_service/Behavioral';
import { JobDescription } from '../../../../../../../../utils/types/JobOpenings';
import { generateBEIQuestions } from '../Constants';

interface Props {
  jobDescription?: JobDescription;
  selectedAttributes?: string[];
  shouldGenerate?: boolean;
  assessmentID: string;
  questions: any[];
  setQuestions: (value: ((prevState: any[]) => any[]) | any[]) => void;
  editingTimeLimit: number;
  setEditingTimeLimit: (value: (((prevState: number) => number) | number)) => void;
}

export const BEIQuestionsContent: React.FC<Props> = ({
  jobDescription,
  selectedAttributes,
  shouldGenerate,
  assessmentID,
  questions,
  setQuestions,
  editingTimeLimit,
  setEditingTimeLimit,
}) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editedQuestion, setEditedQuestion] = useState('');

  // Generate questions only if shouldGenerate is true and selectedAttributes exist
  useEffect(() => {
    if (shouldGenerate && selectedAttributes && selectedAttributes.length > 0) {
      setQuestions(generateBEIQuestions(selectedAttributes));
    }
  }, [shouldGenerate, selectedAttributes, setQuestions]);

  const handleEditClick = (questionItem: any) => {
    setEditingId(questionItem.id);
    setEditedQuestion(questionItem.question);
    setEditingTimeLimit(questionItem.timeLimit);
  };

  const handleSaveEdit = (questionId: number) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) =>
        q.id === questionId
          ? { ...q, question: editedQuestion, timeLimit: editingTimeLimit }
          : q,
      ),
    );
    setEditingId(null);
    setEditedQuestion('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedQuestion('');
    setEditingTimeLimit(editingTimeLimit);
  };

  const saveBEIQuestions = async () => {
    if (!questions || questions.length === 0) return;
    const payload = {
      assessmentId: assessmentID,
      questions: questions.map((q) => ({
        question: q.question,
        attribute: q.attribute,
      })),
      timeLimit: editingTimeLimit,
    };

    try {
      const response = await assessmentBEIQuestion(payload);
      if (response.ok) {
        toast.success('BEI questions saved successfully!', {
          position: 'bottom-center',
        });
      } else {
        toast.error('Unable to complete the request at this time.', {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      toast.error('Unable to complete the request at this time.', {
        position: 'bottom-center',
      });
    }
  };

  return (
    <Box sx={{ flex: 1, p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Typography variant='h6' sx={{ fontWeight: 600, color: 'primary.main' }}>
          BEI Questions
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
        >
          {questions.length > 0 && (
            <Chip
              label={`${questions.length} questions generated`}
              size='small'
              sx={{ bgcolor: 'grey.100', color: 'text.secondary', fontWeight: 500 }}
            />
          )}
          <Button
            variant='contained'
            onClick={saveBEIQuestions}
            disabled={
              !jobDescription?.behavioralAttributes ||
              jobDescription.behavioralAttributes.length === 0 ||
              questions.length === 0 ||
              editingId !== null
            }
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Save Questions
          </Button>
        </Stack>
      </Box>

      {/* Global Time Limit Control */}
      {questions.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            background: 'linear-gradient(90deg, rgba(238,242,255,0.6) 0%, rgba(238,242,255,0.2) 100%)',
            border: '1px solid',
            borderColor: 'primary.light',
            borderRadius: 2,
            p: { xs: 2, sm: 2.5 },
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Stack direction='row' spacing={2} alignItems='center'>
              <Avatar
                sx={{
                  bgcolor: (theme) => `${theme.palette.primary.main}15`,
                  color: 'primary.main',
                  width: 40,
                  height: 40,
                }}
              >
                <Clock size={20} />
              </Avatar>
              <Box>
                <Typography variant='subtitle2' sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Global Time Limit
                </Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  Applies to all questions
                </Typography>
              </Box>
            </Stack>

            <Stack direction='row' spacing={1.5} alignItems='center'>
              <TextField
                type='number'
                size='small'
                value={editingTimeLimit}
                onChange={(e) => setEditingTimeLimit(Number(e.target.value))}
                disabled={editingId !== null}
                inputProps={{ min: 30, max: 600, style: { textAlign: 'center', width: 70 } }}
              />
              <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                seconds
              </Typography>
            </Stack>
          </Box>
        </Paper>
      )}

      {questions.length > 0 ? (
        <Stack spacing={2}>
          {questions.map((questionItem) => (
            <Paper
              key={questionItem.id}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                p: { xs: 2, sm: 2.5 },
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 1,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'common.white',
                    width: 36,
                    height: 36,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  {questionItem.id}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Chip
                    label={questionItem.attribute}
                    size='small'
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      bgcolor: (theme) => `${theme.palette.primary.main}15`,
                      color: 'primary.main',
                      mb: 1.5,
                    }}
                  />

                  {editingId === questionItem.id ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={editedQuestion}
                        onChange={(e) => setEditedQuestion(e.target.value)}
                        autoFocus
                      />
                      <Stack direction='row' spacing={1}>
                        <Button
                          variant='contained'
                          size='small'
                          onClick={() => handleSaveEdit(questionItem.id)}
                          sx={{ textTransform: 'none' }}
                        >
                          Save
                        </Button>
                        <Button
                          variant='outlined'
                          size='small'
                          color='inherit'
                          onClick={handleCancelEdit}
                          sx={{ textTransform: 'none' }}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                      <Typography variant='body2' sx={{ color: 'text.primary', lineHeight: 1.6, flex: 1 }}>
                        {questionItem.question}
                      </Typography>
                      <IconButton
                        size='small'
                        onClick={() => handleEditClick(questionItem)}
                        title='Edit question'
                        sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                      >
                        <Edit2 size={16} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: 'grey.100',
              color: 'grey.400',
              mx: 'auto',
              mb: 2,
            }}
          >
            <HelpCircle size={28} />
          </Avatar>
          {!jobDescription?.behavioralAttributes ||
          jobDescription.behavioralAttributes.length === 0 ? (
            <>
              <Typography variant='h6' sx={{ fontWeight: 500, mb: 1, color: 'text.primary' }}>
                No behavioral attributes found
              </Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                Add behavioral attributes to the job description to generate BEI questions.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant='h6' sx={{ fontWeight: 500, mb: 1, color: 'text.primary' }}>
                Ready to generate BEI questions
              </Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                Select behavioral attributes from the sidebar and click &quot;Generate Questions&quot; to create BEI questions.
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* Information Notice Box */}
      <Paper
        elevation={0}
        sx={{
          mt: 4,
          bgcolor: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 2,
          p: { xs: 2, sm: 3 },
        }}
      >
        <Stack direction='row' spacing={2} alignItems='flex-start'>
          <Info size={22} style={{ flexShrink: 0, marginTop: 2, color: '#2563eb' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1 }}>
              Adaptive BEI Follow-up Questions
            </Typography>
            <Stack spacing={1} sx={{ color: '#1e40af' }}>
              <Typography variant='body2'>
                Each main BEI question can generate up to{' '}
                <strong>3 follow-up questions</strong>. These follow-up
                questions are automatically enabled when the candidate&apos;s
                response to the main question does not meet the minimum scoring
                threshold.
              </Typography>
              <Typography variant='body2'>
                The system analyzes the answer using the{' '}
                <strong>STAR methodology</strong> and activates relevant
                follow-up questions to further probe areas where the response
                was weak or incomplete.
              </Typography>
              <Typography variant='body2'>
                This adaptive questioning approach ensures a comprehensive
                assessment of the candidate&apos;s behavioral competencies while
                maintaining interview efficiency.
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};
